import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Stepper,
  Step,
  StepLabel,
  Box,
  TextField,
  Grid,
  Typography,
  Autocomplete,
  Card,
  CardContent,
  Chip,
  Divider,
  Alert,
  IconButton,
  InputAdornment,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  FormLabel,
  MenuItem,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
} from '@mui/material';
import {
  Close,
  Person,
  Hotel,
  CalendarToday,
  Payment,
  CheckCircle,
  Bed,
  Wifi,
  Tv,
  AcUnit,
  Bathtub,
  Search,
  Add,
} from '@mui/icons-material';
import { format, differenceInDays, addDays, parseISO } from 'date-fns';

// Mock data - in real app, this would come from API/state management
const mockRooms = [
  { id: 'R101', number: '101', type: 'Deluxe', floor: 1, status: 'Available', price: 150, capacity: 2, amenities: ['Wifi', 'TV', 'AC'] },
  { id: 'R102', number: '102', type: 'Standard', floor: 1, status: 'Available', price: 100, capacity: 2, amenities: ['Wifi', 'TV'] },
  { id: 'R201', number: '201', type: 'Suite', floor: 2, status: 'Available', price: 250, capacity: 4, amenities: ['Wifi', 'TV', 'AC', 'Bathtub'] },
  { id: 'R202', number: '202', type: 'Deluxe', floor: 2, status: 'Available', price: 150, capacity: 2, amenities: ['Wifi', 'TV', 'AC'] },
  { id: 'R302', number: '302', type: 'Deluxe', floor: 3, status: 'Available', price: 150, capacity: 2, amenities: ['Wifi', 'TV', 'AC'] },
  { id: 'R303', number: '303', type: 'Standard', floor: 3, status: 'Available', price: 100, capacity: 2, amenities: ['Wifi', 'TV'] },
];

const mockCustomers = [
  { id: 'C001', name: 'John Doe', email: 'john@email.com', phone: '123-456-7890', idNumber: 'ID12345' },
  { id: 'C002', name: 'Jane Smith', email: 'jane@email.com', phone: '987-654-3210', idNumber: 'ID67890' },
  { id: 'C003', name: 'Alice Brown', email: 'alice@email.com', phone: '555-123-4567', idNumber: 'ID11111' },
  { id: 'C004', name: 'Bob Wilson', email: 'bob@email.com', phone: '555-987-6543', idNumber: 'ID22222' },
];

const steps = ['Guest Details', 'Room Selection', 'Booking Details', 'Payment & Confirmation'];

const amenityIcons = {
  Wifi: <Wifi fontSize="small" />,
  TV: <Tv fontSize="small" />,
  AC: <AcUnit fontSize="small" />,
  Bathtub: <Bathtub fontSize="small" />,
};

export default function NewBookingDialog({ open, onClose, onBookingCreated }) {
  const [activeStep, setActiveStep] = useState(0);
  const [errors, setErrors] = useState({});

  // Guest details
  const [customerType, setCustomerType] = useState('existing'); // 'existing' or 'new'
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [newCustomer, setNewCustomer] = useState({
    name: '',
    email: '',
    phone: '',
    idNumber: '',
    address: '',
  });

  // Room selection
  const [selectedRoom, setSelectedRoom] = useState(null);
  const [roomFilter, setRoomFilter] = useState('all'); // 'all', 'standard', 'deluxe', 'suite'

  // Booking details
  const [checkInDate, setCheckInDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [checkOutDate, setCheckOutDate] = useState(format(addDays(new Date(), 1), 'yyyy-MM-dd'));
  const [numberOfGuests, setNumberOfGuests] = useState(1);
  const [specialRequests, setSpecialRequests] = useState('');

  // Payment details
  const [paymentMethod, setPaymentMethod] = useState('credit_card');
  const [depositAmount, setDepositAmount] = useState('');
  const [paymentStatus, setPaymentStatus] = useState('pending');

  // Calculated values
  const [nights, setNights] = useState(1);
  const [totalAmount, setTotalAmount] = useState(0);
  const [taxAmount, setTaxAmount] = useState(0);
  const [grandTotal, setGrandTotal] = useState(0);

  // Calculate nights and amounts
  useEffect(() => {
    if (checkInDate && checkOutDate) {
      const checkIn = parseISO(checkInDate);
      const checkOut = parseISO(checkOutDate);
      const nightsCount = differenceInDays(checkOut, checkIn);
      setNights(nightsCount > 0 ? nightsCount : 1);
    }
  }, [checkInDate, checkOutDate]);

  useEffect(() => {
    if (selectedRoom && nights > 0) {
      const subtotal = selectedRoom.price * nights;
      const tax = subtotal * 0.1; // 10% tax
      setTotalAmount(subtotal);
      setTaxAmount(tax);
      setGrandTotal(subtotal + tax);
      setDepositAmount(subtotal.toString());
    }
  }, [selectedRoom, nights]);

  const validateStep = (step) => {
    const newErrors = {};

    switch (step) {
      case 0: // Guest Details
        if (customerType === 'existing' && !selectedCustomer) {
          newErrors.customer = 'Please select a customer';
        } else if (customerType === 'new') {
          if (!newCustomer.name.trim()) newErrors.name = 'Name is required';
          if (!newCustomer.email.trim()) newErrors.email = 'Email is required';
          if (!newCustomer.phone.trim()) newErrors.phone = 'Phone is required';
          if (!newCustomer.idNumber.trim()) newErrors.idNumber = 'ID number is required';
        }
        break;

      case 1: // Room Selection
        if (!selectedRoom) {
          newErrors.room = 'Please select a room';
        }
        break;

      case 2: // Booking Details
        if (!checkInDate) newErrors.checkInDate = 'Check-in date is required';
        if (!checkOutDate) newErrors.checkOutDate = 'Check-out date is required';
        if (checkInDate && checkOutDate && parseISO(checkOutDate) <= parseISO(checkInDate)) {
          newErrors.checkOutDate = 'Check-out must be after check-in';
        }
        if (!numberOfGuests || numberOfGuests < 1) {
          newErrors.numberOfGuests = 'Number of guests is required';
        }
        if (selectedRoom && numberOfGuests > selectedRoom.capacity) {
          newErrors.numberOfGuests = `Room capacity is ${selectedRoom.capacity} guests`;
        }
        break;

      case 3: // Payment
        if (!depositAmount || parseFloat(depositAmount) < 0) {
          newErrors.depositAmount = 'Valid deposit amount is required';
        }
        break;

      default:
        break;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateStep(activeStep)) {
      setActiveStep((prevActiveStep) => prevActiveStep + 1);
    }
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
    setErrors({});
  };

  const handleClose = () => {
    // Reset all states
    setActiveStep(0);
    setCustomerType('existing');
    setSelectedCustomer(null);
    setNewCustomer({ name: '', email: '', phone: '', idNumber: '', address: '' });
    setSelectedRoom(null);
    setCheckInDate(format(new Date(), 'yyyy-MM-dd'));
    setCheckOutDate(format(addDays(new Date(), 1), 'yyyy-MM-dd'));
    setNumberOfGuests(1);
    setSpecialRequests('');
    setPaymentMethod('credit_card');
    setDepositAmount('');
    setPaymentStatus('pending');
    setErrors({});
    onClose();
  };

  const handleCreateBooking = () => {
    if (!validateStep(activeStep)) return;

    const bookingData = {
      id: `BK${String(Math.floor(Math.random() * 10000)).padStart(3, '0')}`,
      guest: customerType === 'existing' ? selectedCustomer.name : newCustomer.name,
      email: customerType === 'existing' ? selectedCustomer.email : newCustomer.email,
      phone: customerType === 'existing' ? selectedCustomer.phone : newCustomer.phone,
      room: `${selectedRoom.type} ${selectedRoom.number}`,
      checkIn: checkInDate,
      checkOut: checkOutDate,
      nights,
      numberOfGuests,
      specialRequests,
      amount: `$${grandTotal.toFixed(2)}`,
      status: paymentStatus === 'paid' ? 'Confirmed' : 'Pending',
      paymentMethod,
      depositAmount: parseFloat(depositAmount),
      createdAt: new Date().toISOString(),
    };

    console.log('Creating booking:', bookingData);
    
    if (onBookingCreated) {
      onBookingCreated(bookingData);
    }

    handleClose();
  };

  const filteredRooms = mockRooms.filter((room) => {
    if (roomFilter === 'all') return true;
    return room.type.toLowerCase() === roomFilter;
  });

  const getStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <Box>
            <FormControl component="fieldset" fullWidth sx={{ mb: 3 }}>
              <FormLabel component="legend">Customer Type</FormLabel>
              <RadioGroup
                row
                value={customerType}
                onChange={(e) => {
                  setCustomerType(e.target.value);
                  setErrors({});
                }}
              >
                <FormControlLabel value="existing" control={<Radio />} label="Existing Customer" />
                <FormControlLabel value="new" control={<Radio />} label="New Customer" />
              </RadioGroup>
            </FormControl>

            {customerType === 'existing' ? (
              <Autocomplete
                options={mockCustomers}
                getOptionLabel={(option) => `${option.name} - ${option.email}`}
                value={selectedCustomer}
                onChange={(event, newValue) => {
                  setSelectedCustomer(newValue);
                  setErrors({});
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Select Customer"
                    placeholder="Search by name or email"
                    error={!!errors.customer}
                    helperText={errors.customer}
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <>
                          <InputAdornment position="start">
                            <Search />
                          </InputAdornment>
                          {params.InputProps.startAdornment}
                        </>
                      ),
                    }}
                  />
                )}
                renderOption={(props, option) => (
                  <li {...props}>
                    <Box>
                      <Typography variant="body1">{option.name}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {option.email} • {option.phone}
                      </Typography>
                    </Box>
                  </li>
                )}
              />
            ) : (
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Full Name"
                    value={newCustomer.name}
                    onChange={(e) => setNewCustomer({ ...newCustomer, name: e.target.value })}
                    error={!!errors.name}
                    helperText={errors.name}
                    required
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Email"
                    type="email"
                    value={newCustomer.email}
                    onChange={(e) => setNewCustomer({ ...newCustomer, email: e.target.value })}
                    error={!!errors.email}
                    helperText={errors.email}
                    required
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Phone Number"
                    value={newCustomer.phone}
                    onChange={(e) => setNewCustomer({ ...newCustomer, phone: e.target.value })}
                    error={!!errors.phone}
                    helperText={errors.phone}
                    required
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="ID Number"
                    value={newCustomer.idNumber}
                    onChange={(e) => setNewCustomer({ ...newCustomer, idNumber: e.target.value })}
                    error={!!errors.idNumber}
                    helperText={errors.idNumber}
                    required
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Address"
                    multiline
                    rows={2}
                    value={newCustomer.address}
                    onChange={(e) => setNewCustomer({ ...newCustomer, address: e.target.value })}
                  />
                </Grid>
              </Grid>
            )}

            {selectedCustomer && (
              <Card sx={{ mt: 2, bgcolor: '#f5f5f5' }}>
                <CardContent>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Selected Customer
                  </Typography>
                  <Typography variant="h6">{selectedCustomer.name}</Typography>
                  <Typography variant="body2">{selectedCustomer.email}</Typography>
                  <Typography variant="body2">{selectedCustomer.phone}</Typography>
                  <Typography variant="body2">ID: {selectedCustomer.idNumber}</Typography>
                </CardContent>
              </Card>
            )}
          </Box>
        );

      case 1:
        return (
          <Box>
            <Box display="flex" gap={1} mb={3}>
              <Button
                size="small"
                variant={roomFilter === 'all' ? 'contained' : 'outlined'}
                onClick={() => setRoomFilter('all')}
              >
                All Rooms
              </Button>
              <Button
                size="small"
                variant={roomFilter === 'standard' ? 'contained' : 'outlined'}
                onClick={() => setRoomFilter('standard')}
              >
                Standard
              </Button>
              <Button
                size="small"
                variant={roomFilter === 'deluxe' ? 'contained' : 'outlined'}
                onClick={() => setRoomFilter('deluxe')}
              >
                Deluxe
              </Button>
              <Button
                size="small"
                variant={roomFilter === 'suite' ? 'contained' : 'outlined'}
                onClick={() => setRoomFilter('suite')}
              >
                Suite
              </Button>
            </Box>

            {errors.room && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {errors.room}
              </Alert>
            )}

            <Grid container spacing={2}>
              {filteredRooms.map((room) => (
                <Grid item xs={12} sm={6} key={room.id}>
                  <Card
                    sx={{
                      cursor: 'pointer',
                      border: selectedRoom?.id === room.id ? 2 : 1,
                      borderColor: selectedRoom?.id === room.id ? 'primary.main' : 'divider',
                      '&:hover': {
                        borderColor: 'primary.main',
                        boxShadow: 2,
                      },
                    }}
                    onClick={() => {
                      setSelectedRoom(room);
                      setErrors({});
                    }}
                  >
                    <CardContent>
                      <Box display="flex" justifyContent="space-between" alignItems="start" mb={1}>
                        <Box>
                          <Typography variant="h6" fontWeight="bold">
                            Room {room.number}
                          </Typography>
                          <Chip label={room.type} size="small" color="primary" sx={{ mt: 0.5 }} />
                        </Box>
                        {selectedRoom?.id === room.id && (
                          <CheckCircle color="primary" />
                        )}
                      </Box>
                      <Box display="flex" alignItems="center" gap={1} mb={1}>
                        <Bed fontSize="small" color="action" />
                        <Typography variant="body2">Up to {room.capacity} guests</Typography>
                      </Box>
                      <Box display="flex" gap={0.5} mb={2} flexWrap="wrap">
                        {room.amenities.map((amenity) => (
                          <Chip
                            key={amenity}
                            icon={amenityIcons[amenity]}
                            label={amenity}
                            size="small"
                            variant="outlined"
                          />
                        ))}
                      </Box>
                      <Typography variant="h6" color="primary" fontWeight="bold">
                        ${room.price}
                        <Typography component="span" variant="caption" color="text.secondary">
                          /night
                        </Typography>
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Box>
        );

      case 2:
        return (
          <Box>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Check-in Date"
                  type="date"
                  value={checkInDate}
                  onChange={(e) => setCheckInDate(e.target.value)}
                  error={!!errors.checkInDate}
                  helperText={errors.checkInDate}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ min: format(new Date(), 'yyyy-MM-dd') }}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Check-out Date"
                  type="date"
                  value={checkOutDate}
                  onChange={(e) => setCheckOutDate(e.target.value)}
                  error={!!errors.checkOutDate}
                  helperText={errors.checkOutDate}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ min: checkInDate }}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Number of Guests"
                  type="number"
                  value={numberOfGuests}
                  onChange={(e) => setNumberOfGuests(parseInt(e.target.value) || 1)}
                  error={!!errors.numberOfGuests}
                  helperText={errors.numberOfGuests || (selectedRoom ? `Max capacity: ${selectedRoom.capacity}` : '')}
                  inputProps={{ min: 1, max: selectedRoom?.capacity || 10 }}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Number of Nights"
                  value={nights}
                  InputProps={{ readOnly: true }}
                  disabled
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Special Requests"
                  multiline
                  rows={3}
                  value={specialRequests}
                  onChange={(e) => setSpecialRequests(e.target.value)}
                  placeholder="Any special requirements or requests..."
                />
              </Grid>
            </Grid>

            {selectedRoom && (
              <Card sx={{ mt: 3, bgcolor: '#f5f5f5' }}>
                <CardContent>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Booking Summary
                  </Typography>
                  <List dense>
                    <ListItem>
                      <ListItemText primary="Room" secondary={`${selectedRoom.type} ${selectedRoom.number}`} />
                    </ListItem>
                    <ListItem>
                      <ListItemText primary="Dates" secondary={`${checkInDate} to ${checkOutDate}`} />
                    </ListItem>
                    <ListItem>
                      <ListItemText primary="Duration" secondary={`${nights} night${nights > 1 ? 's' : ''}`} />
                    </ListItem>
                    <ListItem>
                      <ListItemText primary="Guests" secondary={`${numberOfGuests} guest${numberOfGuests > 1 ? 's' : ''}`} />
                    </ListItem>
                  </List>
                </CardContent>
              </Card>
            )}
          </Box>
        );

      case 3:
        return (
          <Box>
            <Card sx={{ mb: 3, bgcolor: '#f5f5f5' }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Payment Summary
                </Typography>
                <Divider sx={{ my: 2 }} />
                <Box display="flex" justifyContent="space-between" mb={1}>
                  <Typography>Room Rate ({nights} night{nights > 1 ? 's' : ''})</Typography>
                  <Typography>${totalAmount.toFixed(2)}</Typography>
                </Box>
                <Box display="flex" justifyContent="space-between" mb={1}>
                  <Typography>Tax (10%)</Typography>
                  <Typography>${taxAmount.toFixed(2)}</Typography>
                </Box>
                <Divider sx={{ my: 2 }} />
                <Box display="flex" justifyContent="space-between">
                  <Typography variant="h6">Total Amount</Typography>
                  <Typography variant="h6" color="primary">
                    ${grandTotal.toFixed(2)}
                  </Typography>
                </Box>
              </CardContent>
            </Card>

            <Grid container spacing={3}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  select
                  label="Payment Method"
                  value={paymentMethod}
                  onChange={(e) => setPaymentMethod(e.target.value)}
                >
                  <MenuItem value="credit_card">Credit Card</MenuItem>
                  <MenuItem value="debit_card">Debit Card</MenuItem>
                  <MenuItem value="cash">Cash</MenuItem>
                  <MenuItem value="bank_transfer">Bank Transfer</MenuItem>
                  <MenuItem value="upi">UPI</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Deposit Amount"
                  type="number"
                  value={depositAmount}
                  onChange={(e) => setDepositAmount(e.target.value)}
                  error={!!errors.depositAmount}
                  helperText={errors.depositAmount || `Total: $${grandTotal.toFixed(2)}`}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">$</InputAdornment>,
                  }}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  select
                  label="Payment Status"
                  value={paymentStatus}
                  onChange={(e) => setPaymentStatus(e.target.value)}
                >
                  <MenuItem value="pending">Pending</MenuItem>
                  <MenuItem value="partial">Partial</MenuItem>
                  <MenuItem value="paid">Paid in Full</MenuItem>
                </TextField>
              </Grid>
            </Grid>

            <Card sx={{ mt: 3, bgcolor: '#e3f2fd' }}>
              <CardContent>
                <Typography variant="subtitle2" color="primary" gutterBottom>
                  <CheckCircle fontSize="small" sx={{ verticalAlign: 'middle', mr: 1 }} />
                  Complete Booking Summary
                </Typography>
                <Divider sx={{ my: 2 }} />
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">
                      Guest Name
                    </Typography>
                    <Typography variant="body2" fontWeight="bold">
                      {customerType === 'existing' ? selectedCustomer?.name : newCustomer.name}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">
                      Room
                    </Typography>
                    <Typography variant="body2" fontWeight="bold">
                      {selectedRoom?.type} {selectedRoom?.number}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">
                      Check-in
                    </Typography>
                    <Typography variant="body2" fontWeight="bold">
                      {checkInDate}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">
                      Check-out
                    </Typography>
                    <Typography variant="body2" fontWeight="bold">
                      {checkOutDate}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">
                      Total Amount
                    </Typography>
                    <Typography variant="body2" fontWeight="bold" color="primary">
                      ${grandTotal.toFixed(2)}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">
                      Deposit
                    </Typography>
                    <Typography variant="body2" fontWeight="bold">
                      ${parseFloat(depositAmount || 0).toFixed(2)}
                    </Typography>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Box>
        );

      default:
        return 'Unknown step';
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">Create New Booking</Typography>
          <IconButton onClick={handleClose} size="small">
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {getStepContent(activeStep)}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Box sx={{ flex: '1 1 auto' }} />
        <Button disabled={activeStep === 0} onClick={handleBack}>
          Back
        </Button>
        {activeStep === steps.length - 1 ? (
          <Button variant="contained" onClick={handleCreateBooking}>
            Confirm Booking
          </Button>
        ) : (
          <Button variant="contained" onClick={handleNext}>
            Next
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
